/**
 * Cocoon — Cognito Auth Module (SRP)
 *
 * Sign-in uses USER_SRP_AUTH via amazon-cognito-identity-js: the browser
 * proves knowledge of the password with an SRP exchange (RFC 5054) and the
 * plaintext password never leaves the machine. Previously this module called
 * InitiateAuth USER_PASSWORD_AUTH directly, which put the password in the
 * request body — TLS-protected, but visible to anything that can see the
 * request before encryption (extensions, interceptors, TLS-terminating
 * proxies). The library is used ONLY for the SRP sign-in + challenge dance;
 * token refresh stays a raw REFRESH_TOKEN_AUTH call and tokens live under
 * our own sessionStorage keys, same as before.
 *
 * Flow (unchanged from the UI's point of view):
 *   1. initiateAuth(username, password)
 *      → { challenge: 'NEW_PASSWORD_REQUIRED' | 'SOFTWARE_TOKEN_MFA' | 'MFA_SETUP', session }
 *      → { challenge: null } if fully signed in
 *   2. respondToNewPassword(username, newPassword, session)
 *   3. associateSoftwareToken(session) → { secretCode }
 *      verifySoftwareToken(session, totpCode)
 *      → { complete: true } — with SRP the library answers the MFA_SETUP
 *        challenge itself, so enrollment ENDS the sign-in (no extra TOTP
 *        prompt afterwards, unlike the old hand-rolled flow)
 *   4. respondToMfaChallenge(username, totpCode, session) → { success: true }
 *   5. refreshTokens() → fresh idToken + accessToken
 *
 * The `session` values passed through the UI are vestigial placeholders —
 * challenge state lives in the module-level CognitoUser between calls (a
 * mid-login page refresh restarts the flow, same as before).
 *
 * Tokens stored in sessionStorage (cleared on tab close). The library's own
 * cache is pointed at sessionStorage too — NOT its default localStorage —
 * and logout() clears both sets of keys.
 */

import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
} from "amazon-cognito-identity-js";

const REGION       = process.env.REACT_APP_AWS_REGION           || "us-east-1";
const CLIENT_ID    = process.env.REACT_APP_COGNITO_CLIENT_ID    || "55ud3h89m88vfdl8tt505u7ro2";
const USER_POOL_ID = process.env.REACT_APP_COGNITO_USER_POOL_ID || "us-east-1_gL9qEDBwa";
const COGNITO_URL  = `https://cognito-idp.${REGION}.amazonaws.com/`;

const STORAGE_KEYS = {
  idToken:      "cocoon_id_token",
  accessToken:  "cocoon_access_token",
  refreshToken: "cocoon_refresh_token",
  expiresAt:    "cocoon_expires_at",
};

// Prefix of the library's own cache entries (kept in sessionStorage with ours)
const LIB_KEY_PREFIX = "CognitoIdentityServiceProvider.";

const pool = new CognitoUserPool({
  UserPoolId: USER_POOL_ID,
  ClientId:   CLIENT_ID,
  Storage:    window.sessionStorage,
});

// Carries Cognito challenge state between login steps (module memory only)
let pendingUser = null;

// Returned where the old API returned a Cognito session string — the UI
// stores it and passes it back, but the real state is in pendingUser.
const SESSION_PLACEHOLDER = "srp";

// ── Error normalization ────────────────────────────────────────────────────
// Login.js maps errors by checking message text for Cognito exception names.
// Library errors carry the name in err.code — prepend it so those checks
// keep working (e.g. "CodeMismatchException: Invalid code received…").

function libError(err) {
  const code    = err?.code || err?.name;
  const message = err?.message || "Authentication error";
  const e = new Error(code && !message.includes(code) ? `${code}: ${message}` : message);
  e.code = code;
  return e;
}

function flowLost() {
  return new Error("Login session lost — please sign in again");
}

// Shared handlers for every step that can end in success or another challenge
function challengeCallbacks(username, resolve, reject) {
  return {
    onSuccess: (session) => {
      storeSession(session);
      resolve({ challenge: null });
    },
    onFailure: (err) => reject(libError(err)),
    newPasswordRequired: () =>
      resolve({ challenge: "NEW_PASSWORD_REQUIRED", session: SESSION_PLACEHOLDER, username }),
    mfaSetup: () =>
      resolve({ challenge: "MFA_SETUP", session: SESSION_PLACEHOLDER, username }),
    totpRequired: () =>
      resolve({ challenge: "SOFTWARE_TOKEN_MFA", session: SESSION_PLACEHOLDER, username }),
  };
}

// ── Step 1 — Initiate login (SRP) ──────────────────────────────────────────

export function initiateAuth(username, password) {
  return new Promise((resolve, reject) => {
    pendingUser = new CognitoUser({
      Username: username,
      Pool:     pool,
      Storage:  window.sessionStorage,
    });
    pendingUser.setAuthenticationFlowType("USER_SRP_AUTH");

    const details = new AuthenticationDetails({ Username: username, Password: password });
    pendingUser.authenticateUser(details, challengeCallbacks(username, resolve, reject));
  });
}

// ── Step 2 — Force new password (first login only) ─────────────────────────

export function respondToNewPassword(username, newPassword, _session) {
  return new Promise((resolve, reject) => {
    if (!pendingUser) return reject(flowLost());
    pendingUser.completeNewPasswordChallenge(
      newPassword,
      {}, // no required attributes in this pool
      challengeCallbacks(username, resolve, reject)
    );
  });
}

// ── Step 3a — MFA Setup: get TOTP secret ──────────────────────────────────

export function associateSoftwareToken(_session) {
  return new Promise((resolve, reject) => {
    if (!pendingUser) return reject(flowLost());
    pendingUser.associateSoftwareToken({
      associateSecretCode: (secretCode) =>
        resolve({ secretCode, session: SESSION_PLACEHOLDER }),
      onFailure: (err) => reject(libError(err)),
    });
  });
}

// ── Step 3b — MFA Setup: verify the TOTP code ─────────────────────────────

export function verifySoftwareToken(_session, totpCode) {
  return new Promise((resolve, reject) => {
    if (!pendingUser) return reject(flowLost());
    pendingUser.verifySoftwareToken(totpCode.trim(), "Cocoon Authenticator", {
      onSuccess: (result) => {
        // In the sign-in flow the library answers the MFA_SETUP challenge
        // itself and hands back a full session — sign-in is complete.
        if (result && typeof result.getIdToken === "function") {
          storeSession(result);
          resolve({ complete: true });
        } else {
          resolve({ complete: false, session: SESSION_PLACEHOLDER });
        }
      },
      onFailure: (err) => reject(libError(err)),
    });
  });
}

// ── Step 4 — Respond to MFA challenge (every login) ────────────────────────

export function respondToMfaChallenge(_username, totpCode, _session) {
  return new Promise((resolve, reject) => {
    if (!pendingUser) return reject(flowLost());
    pendingUser.sendMFACode(
      totpCode.trim(),
      {
        onSuccess: (session) => {
          storeSession(session);
          resolve({ success: true });
        },
        onFailure: (err) => reject(libError(err)),
      },
      "SOFTWARE_TOKEN_MFA"
    );
  });
}

// ── Token storage ──────────────────────────────────────────────────────────

// Library path: a CognitoUserSession from a completed SRP sign-in
function storeSession(session) {
  const idToken = session.getIdToken();
  sessionStorage.setItem(STORAGE_KEYS.idToken,     idToken.getJwtToken());
  sessionStorage.setItem(STORAGE_KEYS.accessToken, session.getAccessToken().getJwtToken());

  const refreshToken = session.getRefreshToken();
  if (refreshToken && refreshToken.getToken()) {
    sessionStorage.setItem(STORAGE_KEYS.refreshToken, refreshToken.getToken());
  }

  // getExpiration() is the JWT exp claim in epoch seconds; keep a 60s buffer
  const expiresAt = idToken.getExpiration() * 1000 - 60 * 1000;
  sessionStorage.setItem(STORAGE_KEYS.expiresAt, String(expiresAt));

  pendingUser = null; // flow complete
}

// Raw-API path: an AuthenticationResult from REFRESH_TOKEN_AUTH
function storeTokens(result) {
  sessionStorage.setItem(STORAGE_KEYS.idToken,     result.IdToken);
  sessionStorage.setItem(STORAGE_KEYS.accessToken, result.AccessToken);
  if (result.RefreshToken) {
    sessionStorage.setItem(STORAGE_KEYS.refreshToken, result.RefreshToken);
  }
  const expiresAt = Date.now() + (result.ExpiresIn - 60) * 1000; // 60s buffer
  sessionStorage.setItem(STORAGE_KEYS.expiresAt, String(expiresAt));
}

export function getIdToken() {
  return sessionStorage.getItem(STORAGE_KEYS.idToken);
}

export function isTokenExpired() {
  const expiresAt = Number(sessionStorage.getItem(STORAGE_KEYS.expiresAt) || 0);
  return Date.now() > expiresAt;
}

export function isAuthenticated() {
  return !!getIdToken() && !isTokenExpired();
}

// ── Silent token refresh ───────────────────────────────────────────────────
// Kept as a raw REFRESH_TOKEN_AUTH call — no SRP involved in refresh, and it
// avoids round-tripping tokens through the library's abstractions.

async function cognitoRequest(action, body) {
  const res = await fetch(COGNITO_URL, {
    method: "POST",
    headers: {
      "Content-Type":  "application/x-amz-json-1.1",
      "X-Amz-Target": `AWSCognitoIdentityProviderService.${action}`,
    },
    body: JSON.stringify(body),
  });

  const data = await res.json();

  if (!res.ok) {
    const code    = data.__type || data.code || "UnknownError";
    const message = data.message || "Authentication error";
    const err     = new Error(message);
    err.code      = code;
    throw err;
  }

  return data;
}

export async function refreshTokens() {
  const refreshToken = sessionStorage.getItem(STORAGE_KEYS.refreshToken);
  if (!refreshToken) throw new Error("No refresh token — please log in again");

  try {
    const data = await cognitoRequest("InitiateAuth", {
      AuthFlow:       "REFRESH_TOKEN_AUTH",
      ClientId:       CLIENT_ID,
      AuthParameters: { REFRESH_TOKEN: refreshToken },
    });

    if (data.AuthenticationResult) {
      storeTokens(data.AuthenticationResult);
      return true;
    }
  } catch (err) {
    // Refresh token expired or revoked — force full re-login
    logout();
    throw new Error("Session expired — please log in again");
  }

  return false;
}

// ── Logout ─────────────────────────────────────────────────────────────────

export function logout() {
  Object.values(STORAGE_KEYS).forEach(key => sessionStorage.removeItem(key));

  // Also clear the library's cached entries (same sessionStorage)
  for (let i = sessionStorage.length - 1; i >= 0; i--) {
    const key = sessionStorage.key(i);
    if (key && key.startsWith(LIB_KEY_PREFIX)) sessionStorage.removeItem(key);
  }

  pendingUser = null;
}

// ── QR code URL helper ─────────────────────────────────────────────────────
// Generates the otpauth:// URI that QR code scanners understand

export function getTotpUri(username, secretCode) {
  const issuer  = encodeURIComponent("Cocoon");
  const account = encodeURIComponent(username);
  return `otpauth://totp/${issuer}:${account}?secret=${secretCode}&issuer=${issuer}`;
}
